import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useWellState } from '../../../context/well-context'
import { useGetWellData, useSaveWellData } from '../../../hooks/use-api'
import useEditForm from '../../../hooks/use-edit-form'
import WellEditButtons from '../../wells/well-edit-buttons'
import { useWellId } from '../../../hooks/use-well-id'
import ConfirmTransition from '../../confirm-transition'
import { useNavigate } from 'react-router-dom'
import { CheckableField, FormField, InlineNotification, RecordFooter } from '@msaf/core-react'

interface DetailsTabEditProps {
  setWellCanAddStratum?: Dispatch<SetStateAction<boolean | undefined>>
}

export default function DetailsTabEdit({ setWellCanAddStratum }: DetailsTabEditProps) {
  const {
    wellState: { isDraft },
  } = useWellState()
  const navigate = useNavigate()
  const [isDirty, setIsDirty] = useState<boolean>(false)
  const wellId = useWellId()

  const {
    wellState: { isLegacyWell },
  } = useWellState()

  const url = `wells/${wellId}/lithology/edit`
  const continueUrl = `/wells/${wellId}/edit/wellhead-security`
  const cancelUrl = isDraft ? '/dashboard' : `/wells/${wellId}/view/lithology`

  const stateQueryResult = useGetWellData(url)
  const saveMutationResult = useSaveWellData(url)

  const { fields, isLoading, setValue, handleSave } = useEditForm({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  // If well type is legacy set hasNoStrataRecords by default but do not prevent user from actively selecting
  useEffect(() => {
    if (fields.hasNoStrataRecords === false) {
      return
    }
  }, [isLegacyWell, setValue, fields.hasNoStrataRecords])

  const handleCheckBox = (checked: boolean) => {
    // If checkbox is checked cannot add stratum
    if (setWellCanAddStratum) setWellCanAddStratum(!checked)
    if (setValue) {
      setValue('hasNoStrataRecords', checked)
    }
  }

  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <FormField label="No strata have been added. Click 'Add new' to start adding strata." labelId='no-strata-label'>
        {!fields.isStrataRequired ? (
          <CheckableField
            isSkeleton={isLoading}
            type='checkbox'
            id='no-strata-to-be-added-id'
            name='no-strata-to-be-added'
            describedBy='no-strata-label'
            label='There is no strata information available for this record'
            checked={fields.hasNoStrataRecords === true ?? false}
            onChange={(e) => handleCheckBox(e.target.checked)}
          />
        ) : (
          <InlineNotification isDismissable={false} messageType='info'>
            At least one strata layer must be added for existing or decommissioned well.
          </InlineNotification>
        )}
      </FormField>

      <RecordFooter areActionsFixed>
        <WellEditButtons
          actions={[
            {
              label: 'Save and continue',
              buttonStyle: 'primary',
              onClick: () =>
                handleSave(async (id, refetch) => {
                  await refetch?.()
                  navigate(continueUrl, { state: { isProgrammaticTransition: true } })
                }),
            },
            {
              label: 'Save',
              buttonStyle: 'primary',
              onClick: () => handleSave(async (_id, refetch) => await refetch?.()),
            },
            {
              label: 'Cancel',
              buttonStyle: 'text-action',
              onClick: () => navigate(cancelUrl),
            },
          ]}
        />
      </RecordFooter>
    </ConfirmTransition>
  )
}
